import { Check, Copy } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const COPIED_RESET_DELAY = 1500;

interface CopyButtonProps {
  /** Text written to the clipboard on click. */
  value: string;
  /** Already translated accessible name, e.g. t("card_copyEmail"). */
  label: string;
  className?: string;
}

/** Icon-only copy button that swaps to a check mark for a moment after copying. */
export const CopyButton = ({ value, label, className }: CopyButtonProps) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setCopied(false), COPIED_RESET_DELAY);
  };

  return (
    <Button
      size="icon"
      variant="ghost"
      className={cn("h-7 w-7", className)}
      aria-label={label}
      onClick={handleCopy}
    >
      <span className="relative inline-flex">
        <Copy
          aria-hidden
          className={cn(
            "transition-all duration-200 motion-reduce:transition-none",
            copied ? "scale-0 opacity-0" : "scale-100 opacity-100"
          )}
        />
        <Check
          aria-hidden
          className={cn(
            "absolute inset-0 text-green-600 transition-all duration-200 motion-reduce:transition-none",
            copied ? "scale-100 opacity-100" : "scale-0 opacity-0"
          )}
        />
      </span>
    </Button>
  );
};
